import { useState, useEffect } from 'react';
import { Users, Bell } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import PostList from '@/components/community/PostList';
import NotificationBell from '@/components/community/NotificationBell';
import { postsAPI } from '@/api/community';
import { getUserData } from '@/utils/auth';
import { trackEvent, attachUserProps } from '@/analytics/analytics';

export default function GuardianCommunityPage() {
  const [activeTab, setActiveTab] = useState('announcements');
  const [refreshKey, setRefreshKey] = useState(0);
  const userData = getUserData('guardian'); // Guardians use 'guardian' portal for auth
  const userType = userData?.user_type || 'guardian';
  const isGuardian = userType === 'guardian';

  const handlePostUpdate = () => {
    setRefreshKey(prev => prev + 1);
    try { trackEvent('guardian_posts_refreshed', attachUserProps({ page: 'guardian_community' })); } catch (e) {}
  };

  // Fetch functions for each tab
  const fetchAnnouncements = (page, pageSize) => {
    return postsAPI.getAnnouncements(page, pageSize);
  };

  const fetchMyFeed = (page, pageSize) => {
    return postsAPI.getMyFeed(page, pageSize);
  };

  const fetchAllPosts = (page, pageSize) => {
    return postsAPI.getAllPosts(page, pageSize);
  };

  return (
    <div className="flex flex-col h-full bg-[hsl(var(--main-bg))] p-6 overflow-y-auto">
      <div className="max-w-4xl mx-auto w-full space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Users className="w-8 h-8 text-[hsl(var(--green-bright))]" />
            <div>
              <h1 className="text-3xl font-bold text-foreground">Community</h1>
              <p className="text-muted-foreground">
                View community posts and announcements (read-only)
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <Badge variant="secondary" className="text-xs">
              Read-only
            </Badge>
          </div>
        </div>

        {/* Three Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); try { trackEvent('guardian_community_tab_change', attachUserProps({ tab: v, page: 'guardian_community' })); } catch (e) {} }} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="announcements">Announcements</TabsTrigger>
            <TabsTrigger value="my-feed">My Feed</TabsTrigger>
            <TabsTrigger value="posts">Posts</TabsTrigger>
          </TabsList>

          <TabsContent value="announcements" className="mt-4">
            <PostList
              key={`announcements-${refreshKey}`}
              fetchFunction={fetchAnnouncements}
              onPostUpdate={handlePostUpdate}
              isGuardian={isGuardian}
            />
          </TabsContent>

          <TabsContent value="my-feed" className="mt-4">
            <PostList
              key={`my-feed-${refreshKey}`}
              fetchFunction={fetchMyFeed}
              onPostUpdate={handlePostUpdate}
              isGuardian={isGuardian}
            />
          </TabsContent>

          <TabsContent value="posts" className="mt-4">
            <PostList
              key={`posts-${refreshKey}`}
              fetchFunction={fetchAllPosts}
              onPostUpdate={handlePostUpdate}
              isGuardian={isGuardian}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
